/**
 * Empty State Component
 *
 * Provides helpful guidance when no data is available
 * Based on Paytronix and Square empty state patterns
 */

import React from 'react';

interface EmptyStateProps {
  illustration?: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  illustration,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}
      role="status"
      aria-label={`${title}. ${description}`}
    >
      {illustration && (
        <div className="w-48 h-48 mb-6" aria-hidden="true">
          {illustration}
        </div>
      )}
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
        {description}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Pre-built empty state illustrations using SVG
 */

export function NoCustomersIllustration() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="60" r="30" fill="#E5E7EB" />
      <path d="M60 140 C 60 110, 140 110, 140 140" fill="#E5E7EB" />
      <circle cx="100" cy="60" r="12" fill="#9CA3AF" />
      <circle cx="90" cy="55" r="3" fill="#4B5563" />
      <circle cx="110" cy="55" r="3" fill="#4B5563" />
      <path d="M95 65 Q 100 70 105 65" stroke="#4B5563" strokeWidth="2" fill="none" />
    </svg>
  );
}

export function NoTransactionsIllustration() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="40" y="60" width="120" height="80" rx="8" fill="#E5E7EB" />
      <rect x="60" y="80" width="80" height="8" rx="4" fill="#9CA3AF" />
      <rect x="60" y="100" width="60" height="8" rx="4" fill="#9CA3AF" />
      <rect x="60" y="120" width="70" height="8" rx="4" fill="#9CA3AF" />
    </svg>
  );
}

export function NoRewardsIllustration() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="100" r="50" fill="#E5E7EB" />
      <path d="M80 100 L95 115 L120 85" stroke="#9CA3AF" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function NoSearchResultsIllustration() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="80" cy="80" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
      <line x1="110" y1="110" x2="140" y2="140" stroke="#E5E7EB" strokeWidth="8" strokeLinecap="round" />
      <line x1="60" y1="60" x2="100" y2="100" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
      <line x1="100" y1="60" x2="60" y2="100" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}
